import React, { FC, memo } from "react";
// libs
import NextImage from "next/image";
import Slider from "react-slick";
import isEqual from "lodash/isEqual";
// utils
import { getTheStars, transformRateCountReviews } from "src/utils/reviews";
import generateSliderSettings from "src/utils/slidersToShow";
// hooks
import { useAppSelector, useWindowSize } from "src/hooks";
// redux
import { dynamicStateFieldSelector } from "src/redux/slices";
// const
import { STORES_RATING_BLOCK } from "src/constants/dynamic";
// types
import { IReview } from "src/types";
import { IDynamicStoresRating } from "src/types/dynamic/home/storesRating";
// theme
import {
  Wrapper,
  TitleWrapper,
  ReviewsContent,
  BottomSection,
  Text,
  RatesSection,
  AppRateTitle,
  Rate,
  Card,
  NumberOfReviews,
  CardWrapper,
  CardTitle,
  RateStars,
  ReviewerDetails,
  ReviewText,
  RatesItem,
  RateDetails,
  TextP,
  TextBox,
} from "./reviews.styles";
import { LargeTitle } from "src/theme/typography";
import { colors } from "src/theme";

const customWidth = {
  customMaxWidth: 2300,
  customLaptopLWidth: 1760,
  customLaptopWidth: 1220,
  customTabletWidth: 768,
};

export interface IReviewsComponent {
  reviews: IReview[];
}

const ReviewsComponent: FC<IReviewsComponent> = ({ reviews }) => {
  const storesRating = useAppSelector(
    dynamicStateFieldSelector<IDynamicStoresRating>(STORES_RATING_BLOCK),
  );

  const size = useWindowSize();

  const settings = generateSliderSettings(size, customWidth);

  let contentHeight = "390px";

  if (size.width > 768 && size.width < 1024) {
    contentHeight = "240px";
  } else if (size.width >= 1024) {
    contentHeight = "450px";
  }

  const renderReviewCard = (reviewItem: IReview) => {
    const reviewerName = reviewItem.is_name_hidden
      ? "Anonymous"
      : reviewItem.reviewer_name || "User";
    const createdAtDate = new Date(reviewItem.created_at);
    const createdAt = `${createdAtDate.getDate()}/${createdAtDate.getMonth()}/${createdAtDate.getFullYear()}`;
    const reviewDetails = `${reviewerName}, ${createdAt}`;

    return (
      <Card key={reviewItem.id}>
        <CardWrapper>
          <CardTitle>{reviewItem.title}</CardTitle>
          <RateDetails>
            <RateStars>
              {getTheStars(reviewItem.rating).map((starSrc, idx) => (
                <NextImage key={idx} src={starSrc} width={15} height={15} />
              ))}
            </RateStars>
            <ReviewerDetails>{reviewDetails}</ReviewerDetails>
          </RateDetails>
          <ReviewText>
            <TextP>{reviewItem.description}</TextP>
          </ReviewText>
        </CardWrapper>
      </Card>
    );
  };

  return (
    <Wrapper>
      <TitleWrapper>
        <LargeTitle textColor={colors.white}>
          Pet owners love PetsApp,
          <br />
          <span style={{ color: colors.mainLight }}>
            here&apos;s what they&apos;ve got to say&hellip;
          </span>
        </LargeTitle>
      </TitleWrapper>

      <ReviewsContent height={contentHeight}>
        <Slider {...settings} centerMode={false} rtl={false}>
          {reviews.map(renderReviewCard)}
        </Slider>

        {size.width >= 1024 && (
          <Slider {...settings} rtl>
            {reviews.map(renderReviewCard)}
          </Slider>
        )}
      </ReviewsContent>

      <BottomSection>
        <Text>
          The highest rated veterinary app <TextBox>in the UK and Ireland</TextBox>
        </Text>

        <RatesSection>
          <RatesItem>
            <AppRateTitle>Apple App Store</AppRateTitle>
            <Rate>{storesRating?.appstore_score}</Rate>
            <NumberOfReviews>
              {transformRateCountReviews(storesRating?.applestore_count)} reviews
            </NumberOfReviews>
          </RatesItem>
          <RatesItem>
            <AppRateTitle>Google Play Store</AppRateTitle>
            <Rate>{storesRating?.google_score}</Rate>
            <NumberOfReviews>
              {transformRateCountReviews(storesRating?.google_count)} reviews
            </NumberOfReviews>
          </RatesItem>
        </RatesSection>
      </BottomSection>
    </Wrapper>
  );
};

export default memo(ReviewsComponent, isEqual);
